import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadMedia } from '../../lib/chunkedUpload';
import { adminApi, mediaUrl } from '../../lib/programsApi';
import { AudioBadge } from './ContentPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReplaceAudioModal = ({ contentId, open, onClose, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState(0);
  const [confirmRollback, setConfirmRollback] = useState(null);
  const [script, setScript] = useState('');
  const [showScript, setShowScript] = useState(false);
  const fileRef = useRef(null);

  const load = () => adminApi.audio(contentId).then((d) => { setInfo(d); setScript(d.ttsScript || ''); }).catch((e) => toast.error(e.message));
  useEffect(() => { if (open && contentId) { setCandidate(null); setConfirmRollback(null); load(); } }, [open, contentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload');
    try {
      const token = localStorage.getItem('rbr_token');
      const asset = await uploadMedia({ file, kind: 'audio', apiUrl: API_URL, token, onProgress: ({ loaded, total }) => setProgress(total ? Math.round((loaded / total) * 100) : 0) });
      setCandidate({ assetId: asset.id, url: asset.url, name: file.name, source: 'recording' });
      toast.success('Recording uploaded — preview it, then Use New Recording');
    } catch (e) { toast.error(e.message || 'Upload failed'); } finally { setBusy(null); setProgress(0); }
  };

  const regenTts = async () => {
    setBusy('tts');
    try {
      const r = await adminApi.audioRegenTts(contentId, script);
      setCandidate({ assetId: r.assetId, url: r.url, name: 'Regenerated TTS', source: 'tts' });
      toast.success('TTS candidate ready — preview it, then commit');
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const commit = async () => {
    setBusy('commit');
    try {
      const r = await adminApi.audioCommit(contentId, { assetId: candidate.assetId, audioSource: candidate.source, expectedUpdatedAt: info.updatedAt });
      setInfo(r.content); setCandidate(null); onChanged?.(r.content);
      toast.success('New audio is live — same content id, previous version kept in history');
    } catch (e) { toast.error(e.message); if (e.status === 409) load(); } finally { setBusy(null); }
  };

  const rollback = async (assetId) => {
    setBusy('rollback');
    try {
      const r = await adminApi.audioRollback(contentId, { assetId, expectedUpdatedAt: info.updatedAt });
      setInfo(r.content); setConfirmRollback(null); onChanged?.(r.content);
      toast.success('Rolled back — the replaced version stays in history');
    } catch (e) { toast.error(e.message); if (e.status === 409) load(); } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="replace-audio-modal">
      <div className="bg-white w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base font-semibold text-stone-800 flex-1">Replace Audio</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="replace-audio-close"><X className="w-4 h-4" /></button>
        </div>
        {!info ? <p className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p> : (
          <>
            <p className="text-sm text-stone-700 font-medium">{info.title}</p>
            <p className="text-[10.5px] font-mono text-stone-400 break-all mb-4">content id {info.id} · v{info.audioVersion} — this id never changes</p>

            <section className="rounded-2xl border border-stone-200 p-3 mb-3" data-testid="replace-audio-current">
              <div className="flex items-center justify-between mb-2"><p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Current</p><AudioBadge source={info.audioSource} testId="replace-audio-current-badge" /></div>
              {info.audioUrl ? <audio controls preload="none" src={mediaUrl(info.audioUrl)} className="h-8 w-full" data-testid="replace-audio-preview-current" /> : <p className="text-xs text-rose-600">No audio attached</p>}
            </section>

            <section className="rounded-2xl border border-dashed border-[#C79A4A]/60 bg-[#FFFCF6] p-3 mb-3" data-testid="replace-audio-new">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">New recording</p>
              {!candidate ? (
                <div className="flex flex-col gap-2">
                  <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="replace-audio-file" />
                  <button type="button" disabled={!!busy} onClick={() => fileRef.current?.click()} className="h-10 rounded-full bg-[#173F35] text-white text-xs font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="replace-audio-upload">
                    {busy === 'upload' ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading {progress}%</> : <><Upload className="w-4 h-4" /> Upload Rowdy Recording</>}
                  </button>
                  {info.ttsScript && (
                    <div>
                      <button type="button" onClick={() => setShowScript((v) => !v)} className="text-[11px] font-semibold text-[#C79A4A]" data-testid="replace-audio-script-toggle">{showScript ? 'Hide TTS script' : 'Edit script & regenerate TTS'}</button>
                      {showScript && (
                        <div className="mt-2 flex flex-col gap-2">
                          <textarea value={script} onChange={(e) => setScript(e.target.value)} rows={6} className="w-full border border-stone-200 rounded-xl p-2 text-xs text-stone-800" data-testid="replace-audio-script" />
                          <button type="button" disabled={!!busy} onClick={regenTts} className="h-9 rounded-full border border-stone-300 text-stone-700 text-xs font-semibold disabled:opacity-50" data-testid="replace-audio-regen-tts">{busy === 'tts' ? 'Synthesizing…' : 'Regenerate TTS candidate'}</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-stone-600">{candidate.name} <AudioBadge source={candidate.source} /></p>
                  <audio controls preload="none" src={mediaUrl(candidate.url)} className="h-8 w-full" data-testid="replace-audio-preview-new" />
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setCandidate(null)} className="h-10 rounded-full border border-stone-300 text-stone-700 text-xs font-semibold" data-testid="replace-audio-cancel">Cancel</button>
                    <button type="button" disabled={!!busy} onClick={commit} className="h-10 rounded-full bg-[#173F35] text-white text-xs font-semibold disabled:opacity-50" data-testid="replace-audio-use-new">{busy === 'commit' ? 'Committing…' : 'Use New Recording'}</button>
                  </div>
                  <p className="text-[11px] text-stone-500">Nothing changes publicly until you confirm. The current version is kept for rollback.</p>
                </div>
              )}
            </section>

            <section data-testid="replace-audio-history">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Version history</p>
              {!info.audioHistory?.length && <p className="text-xs text-stone-400">No previous versions yet.</p>}
              {[...(info.audioHistory || [])].reverse().map((h) => (
                <div key={`${h.assetId}-${h.versionNumber}`} className="border border-stone-200 rounded-xl p-2.5 mb-2" data-testid={`replace-audio-history-${h.versionNumber}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-stone-700">v{h.versionNumber} · <AudioBadge source={h.audioSource} /> · replaced {new Date(h.replacedAt).toLocaleString()} by {h.replacedBy}</p>
                    {confirmRollback === h.assetId ? (
                      <div className="flex gap-1">
                        <button type="button" onClick={() => setConfirmRollback(null)} className="px-2 h-8 rounded-full border border-stone-300 text-stone-700 text-[11px]">Keep current</button>
                        <button type="button" disabled={!!busy} onClick={() => rollback(h.assetId)} className="px-2 h-8 rounded-full bg-rose-600 text-white text-[11px] font-semibold" data-testid={`replace-audio-rollback-confirm-${h.versionNumber}`}>Confirm rollback</button>
                      </div>
                    ) : (
                      <button type="button" disabled={h.assetId === info.audioAssetId} onClick={() => setConfirmRollback(h.assetId)} className="px-2 h-8 rounded-full border border-stone-300 text-stone-700 text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-40" data-testid={`replace-audio-rollback-${h.versionNumber}`}><RotateCcw className="w-3 h-3" /> Rollback</button>
                    )}
                  </div>
                  {h.audioUrl && <audio controls preload="none" src={mediaUrl(h.audioUrl)} className="h-7 w-full mt-2" />}
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </div>
  );
};
